import { formatAmount, formatQuantity, CURRENCIES } from '@pos/business-logic';
import type { Money as MoneyValue, ProductUnit, Qty } from '@pos/types';
import { useLayoutEffect, useRef } from 'react';
import { cn } from '../cn';

/**
 * Money set like a shelf tag: tabular figures, the currency quieter than the number.
 * `size` maps to the type scale; totals use the display steps.
 */
export function Money({
  value,
  currency = 'UZS',
  className,
  symbolClassName,
  hideSymbol,
}: {
  value: MoneyValue;
  currency?: string;
  className?: string;
  symbolClassName?: string;
  hideSymbol?: boolean;
}) {
  const meta = CURRENCIES[currency];
  const amount = formatAmount(value, currency);
  const symbol = meta?.symbol ?? currency;
  return (
    <span className={cn('tnum whitespace-nowrap', className)}>
      {!hideSymbol && meta?.symbolPosition === 'before' && (
        <span className={cn('text-[0.6em] font-medium text-ink-3', symbolClassName)}>{symbol}</span>
      )}
      {amount}
      {!hideSymbol && meta?.symbolPosition !== 'before' && (
        <span className={cn('ml-[0.25em] text-[0.62em] font-medium text-ink-3', symbolClassName)}>
          {symbol}
        </span>
      )}
    </span>
  );
}

export function Quantity({
  value,
  unit,
  className,
}: {
  value: Qty;
  unit: ProductUnit;
  className?: string;
}) {
  return (
    <span className={cn('tnum whitespace-nowrap', className)}>{formatQuantity(value, unit)}</span>
  );
}

/**
 * A sum that never clips.
 *
 * Totals are the numbers people check across a counter, and in soum an
 * ordinary basket already runs to seven digits. A fixed type size is sized to
 * whichever amount the designer happened to try, so the one that matters — the
 * big one — is the one that runs off the edge. This sets the sum at its
 * intended size, measures it, and steps it down only when it does not fit.
 *
 * The line box is set here rather than inherited: at display sizes a sum
 * dropped into a 15px line box is clipped top and bottom by the very box that
 * is guarding the width.
 */
export function FitMoney({
  value,
  max,
  className,
  symbolClassName,
  align = 'left',
}: {
  value: MoneyValue;
  /** Intended size in px. Never exceeded, only reduced. */
  max: number;
  className?: string;
  symbolClassName?: string;
  align?: 'left' | 'right';
}) {
  const box = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const b = box.current;
    const i = inner.current;
    if (!b || !i) return;
    i.style.fontSize = `${max}px`;
    const natural = i.scrollWidth;
    const avail = b.clientWidth;
    if (natural > avail && natural > 0) {
      i.style.fontSize = `${Math.max(20, Math.floor(max * (avail / natural)))}px`;
    }
  }, [value, max]);
  return (
    <div ref={box} className={cn('w-full overflow-hidden', align === 'right' && 'text-right')}>
      <span
        ref={inner}
        className={cn('inline-block whitespace-nowrap', className)}
        style={{ fontSize: max, lineHeight: 1.1 }}
      >
        <Money value={value} symbolClassName={symbolClassName} />
      </span>
    </div>
  );
}
